import { createContext, useEffect, useState } from "react";
import { getProductData } from "./components/productsData";
import { calculateFinalPrice } from "./components/homeComponents/priceUtils";

export const CartContext = createContext({
  items: [],
  getProductQuantity: () => {},
  addOneToCart: () => {},
  removeOneFromCart: () => {},
  deleteFromCart: () => {},
  getTotalCost: () => {},
  setCart: () => {},
  clearCart: () => {},
});

// Compute the correct LINE TOTAL for a product id at a given quantity,
// using the shared pricing rules (bundles + discount). This is the only
// place the cart decides what a line costs, so the price can never go stale.
function lineTotalFor(id, quantity) {
  const product = getProductData(id);
  if (!product) return 0;
  return calculateFinalPrice(
    product.name,
    quantity,
    product.price,
    product.discountPrice
  );
}

export function CartProvider({ children }) {
  const [cartProducts, setCartProducts] = useState([]);

  useEffect(() => {
    const storedCart = JSON.parse(localStorage.getItem("cart"));
    if (storedCart && Array.isArray(storedCart)) {
      // Re-heal any cart saved by an older/buggy version: recompute every
      // line's finalPrice so prices are always current and correct.
      const healed = storedCart.map((item) => ({
        ...item,
        finalPrice: lineTotalFor(item.id, item.quantity),
      }));
      setCartProducts(healed);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(cartProducts));
  }, [cartProducts]);

  function getProductQuantity(id) {
    return cartProducts.find((product) => product.id === id)?.quantity || 0;
  }

  // Note: finalPrice is intentionally ignored as an argument and always
  // recomputed from the resulting quantity, so callers can never push a
  // stale or wrong price into the cart.
  function addOneToCart(id, _finalPrice, quantity = 1) {
    setCartProducts((prevCart) => {
      const existingProduct = prevCart.find((product) => product.id === id);

      if (existingProduct) {
        return prevCart.map((product) => {
          if (product.id !== id) return product;
          const newQuantity = product.quantity + quantity;
          return {
            ...product,
            quantity: newQuantity,
            finalPrice: lineTotalFor(id, newQuantity),
          };
        });
      }

      const productToAdd = getProductData(id);
      if (!productToAdd) return prevCart;
      return [
        ...prevCart,
        {
          ...productToAdd,
          quantity,
          finalPrice: lineTotalFor(id, quantity),
        },
      ];
    });
  }

  function removeOneFromCart(id) {
    const quantity = getProductQuantity(id);
    if (quantity <= 1) {
      deleteFromCart(id);
    } else {
      setCartProducts((prevCart) =>
        prevCart.map((product) => {
          if (product.id !== id) return product;
          const newQuantity = product.quantity - 1;
          return {
            ...product,
            quantity: newQuantity,
            finalPrice: lineTotalFor(id, newQuantity),
          };
        })
      );
    }
  }

  function deleteFromCart(id) {
    setCartProducts((prevCart) =>
      prevCart.filter((currentProduct) => currentProduct.id !== id)
    );
  }

  // finalPrice is already the line total for each item, so the cart total
  // is simply their sum. (The old code multiplied by quantity again, which
  // double-counted every line.)
  function getTotalCost() {
    return cartProducts.reduce(
      (total, cartItem) => total + (Number(cartItem.finalPrice) || 0),
      0
    );
  }

  const setCart = (newCart) => {
    const normalized = (newCart || []).map((item) => ({
      ...item,
      finalPrice: lineTotalFor(item.id, item.quantity),
    }));
    setCartProducts(normalized);
  };

  const clearCart = () => {
    setCartProducts([]);
    localStorage.removeItem("cart");
  };

  const contextValue = {
    items: cartProducts,
    getProductQuantity,
    addOneToCart,
    removeOneFromCart,
    deleteFromCart,
    getTotalCost,
    setCart,
    clearCart,
  };

  return (
    <CartContext.Provider value={contextValue}>{children}</CartContext.Provider>
  );
}

export default CartProvider;
